import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  AuditEventInput,
  AuditEventRecord,
  AuditIngestResponse,
  AuditLogSource,
} from '../model/audit.models';
import { ConnectivityService } from './connectivity.service';
import { DeviceIdentityService } from './device-identity.service';
import { IndexedDbStorageService } from './indexed-db-storage.service';

const AUDIT_QUEUE_KEY = 'tops-offline.auditQueue';
const AUDIT_ENDPOINT = `${environment.backendApiBaseUrl}/audit/events`;
const MAX_QUEUE_SIZE = 5000;
const MAX_BATCH_SIZE = 200;

/**
 * Captures audit events on-device and delivers them to the API.
 *
 * <p>Every event is queued first and sent second, even when online. That ordering is what makes
 * the trail survive a tab close, a crash or a connection that drops mid-flush: an event is only
 * removed from the queue once the server has confirmed it was stored.
 */
@Injectable({ providedIn: 'root' })
export class AuditService {
  private readonly http = inject(HttpClient);
  private readonly storage = inject(IndexedDbStorageService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly deviceIdentity = inject(DeviceIdentityService);

  private queue: AuditEventRecord[] = [];
  private initialized = false;
  private flushing = false;
  private activeTransactionId: string | null = null;

  constructor() {
    void this.initialize();
    this.connectivity.isOnline$.subscribe((isOnline) => {
      if (isOnline) void this.flush();
    });
  }

  /**
   * Opens a scope for a multi-step flow so its events share one id. Outside a scope every
   * event gets its own id, because the stories require one unique id per transaction.
   */
  beginTransaction(): string {
    this.activeTransactionId = this.createId();
    return this.activeTransactionId;
  }

  endTransaction(): void {
    this.activeTransactionId = null;
  }

  transactionId(): string {
    return this.activeTransactionId ?? this.createId();
  }

  /** Queues an event, tagging it ONLINE or OFFLINE according to connectivity at capture time. */
  async record(input: AuditEventInput): Promise<void> {
    await this.initialize();

    const logSource: AuditLogSource = input.logSource ?? (this.connectivity.currentOnline ? 'ONLINE' : 'OFFLINE');
    const event: AuditEventRecord = {
      ...input,
      eventId: input.eventId ?? this.createId(),
      transactionId: input.transactionId ?? this.activeTransactionId ?? this.createId(),
      logSource,
      severity: input.severity ?? 'INFO',
      outcome: input.outcome ?? 'SUCCESS',
      deviceId: this.deviceIdentity.deviceIdSync(),
      deviceInfo: input.deviceInfo ?? this.deviceIdentity.deviceInfo(),
      clientEventTimeUtc: this.nowUtcIso(),
    };

    this.enqueue(event);
    if (this.connectivity.currentOnline) void this.flush();
  }

  /** Fire-and-forget wrapper for call sites that must not await auditing. */
  track(input: AuditEventInput): void {
    void this.record(input);
  }

  pendingCount(): number {
    return this.queue.length;
  }

  /** Sends queued events in batches; safe to call concurrently. */
  async flush(): Promise<void> {
    if (this.flushing || !this.connectivity.currentOnline) return;
    await this.initialize();
    if (this.queue.length === 0) return;

    this.flushing = true;
    try {
      while (this.queue.length > 0 && this.connectivity.currentOnline) {
        const batch = this.queue.slice(0, MAX_BATCH_SIZE);
        const response = await firstValueFrom(
          this.http.post<AuditIngestResponse>(AUDIT_ENDPOINT, {
            events: batch,
            deviceId: this.deviceIdentity.deviceIdSync(),
            deviceInfo: this.deviceIdentity.deviceInfo(),
          }),
        );

        // Accepted, duplicate and rejected are all terminal: retrying them cannot change anything.
        const settled = new Set(batch.map((event) => event.eventId));
        this.queue = this.queue.filter((event) => !settled.has(event.eventId));
        this.persistQueue();

        if (response.received === 0) break;
      }
    } catch {
      // Leave the queue intact; the next reconnect or capture retries it.
    } finally {
      this.flushing = false;
    }
  }

  private async initialize(): Promise<void> {
    if (this.initialized) return;
    await this.storage.ready();
    this.queue = this.readQueue();
    this.initialized = true;
    if (this.connectivity.currentOnline) void this.flush();
  }

  private enqueue(event: AuditEventRecord): void {
    this.queue.push(event);
    if (this.queue.length > MAX_QUEUE_SIZE) {
      this.dropOldestLowSeverity();
    }
    this.persistQueue();
  }

  /**
   * When the queue is full, the least significant events are shed first so that security events
   * are the last thing to be lost rather than the first.
   */
  private dropOldestLowSeverity(): void {
    const overflow = this.queue.length - MAX_QUEUE_SIZE;
    const droppable = ['INFO', 'WARNING'];

    for (let removed = 0; removed < overflow; removed++) {
      const index = this.queue.findIndex((event) => droppable.includes(event.severity));
      this.queue.splice(index >= 0 ? index : 0, 1);
    }
  }

  private readQueue(): AuditEventRecord[] {
    try {
      const raw = this.storage.getItem(AUDIT_QUEUE_KEY);
      const parsed = raw ? (JSON.parse(raw) as AuditEventRecord[]) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private persistQueue(): void {
    try {
      this.storage.setItem(AUDIT_QUEUE_KEY, JSON.stringify(this.queue));
    } catch {
      // Storage quota exhausted; the in-memory queue still flushes for this session.
    }
  }

  private nowUtcIso(): string {
    return new Date().toISOString();
  }

  private createId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
      return crypto.randomUUID();
    }
    return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 14)}`;
  }
}
